import { createTheme, darken, lighten, responsiveFontSizes, ThemeProvider } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import _ from "lodash";
import { UserThemeContext } from "../contexts";
import { userApi } from "./user.queries";

export default function UserThemeProvider({ children }) {
	const useMe = userApi.useMe();
	const user = useMe.data;

	if (!user) {
		return;
	}

	const mode = user?.prefs?.mode == 'dark' ? 'dark' : 'light';
	const colour1 = '#040273';
	const colour2 = '#d5a8ff';

	const dark = mode != 'light'
	let palette = {
		mode: mode,
		primary: {
			main: colour1,
		},
		secondary: {
			main: colour2,//https://mycolor.space/?hex=%23040273&sub=1
		},
		muted: {
			main: '#999999',//https://mycolor.space/?hex=%23040273&sub=1
		}
	};
	palette.text = {
		primary: dark ? "#fcfcfc" : "rgb(0,0,0,0.9)",
		secondary: dark ? "rgba(255, 255, 255, 0.7)" : "rgb(0,0,0,0.7)",
		disabled: dark ? "rgba(255, 255, 255, 0.5)" : "rgb(0,0,0,0.5)",
		icon: dark ? "rgba(255, 255, 255, 0.5)" : "rgb(0,0,0,0.5)",
		link: dark ? lighten(colour1, 0.75) : darken(colour1, 0.75),
	};

	palette.background = {
		paper: dark ? darken(colour1, 0.9) : lighten(colour1, 0.95),
		paper2: dark ? darken(colour1, 0.8) : lighten(colour1, 0.9),
		default: dark ? darken(colour2, 0.9) : lighten(colour2, 0.95),
		today: dark ? '#001f53' : '#b3e8ff',
		weekend: dark ? '#262626' : '#e0e0e0',
		holiday: dark ? '#424242' : '#bdbdbd',
	};

	const defaultSmall = () => {
		return {
			defaultProps: {
				size: 'small',
			}
		};
	};

	const style = (styles) => {
		return {
			styleOverrides: {
				root: styles,
			}
		};
	};

	const defaultTypography = { lineHeight: '1rem', fontWeight: 400 };
	let theme = createTheme({
		spacing: 4,
		colorSchemes: {
			dark: dark,
		},
		typography: {
			fontFamily: 'Quicksand',
			fontSize: 12,
			lineHeight: 1,
			h1: {
				fontSize: '2rem',
				fontFamily: 'Charm',
				lineHeight: 2
			},
			h2: {
				fontSize: '2rem',
				lineHeight: 2
			},
			h3: {
				fontSize: '1.75rem',
				lineHeight: 1.75
			},
			h4: {
				fontSize: '1.5rem',
				lineHeight: 1.5
			},
			h5: {
				fontSize: '1.25rem',
				lineHeight: 1.25,
				fontWeight: 600,
			},
			h6: {
				fontSize: '1rem',
				...defaultTypography,
				fontWeight: 500,
			},
			button: {
				fontSize: '1rem',
				...defaultTypography,
				fontWeight: 600,
			},
			subtitle1: defaultTypography,
			subtitle2: {
				...defaultTypography,
				color: palette.text.secondary,
			},
			body1: defaultTypography,
			body2: defaultTypography,
			caption: defaultTypography,
			overline: defaultTypography,
			inherit: defaultTypography,
			small: {
				...defaultTypography,
				fontSize: '0.75rem',
				fontWeight: 400,
			},
		},
		palette: palette,
		components: {
			MuiAutocomplete: style(({ theme }) => ({
				'.MuiFormControl-root': {
					marginTop: 0,
					marginBottom: 0,
				}
			})),
			MuiButton: {
				size: 'small',
				defaultProps: {
					color: palette.text.link,
				}
			},
			MuiButtonGroup: {
				size: 'small',
			},
			MuiCardContent: style({ '&:last-child': { 'paddingBottom': 0 } }),
			MuiCheckbox: {
				size: 'small',
			},
			MuiDialogActions: style({
				padding: '0 24px 16px 24px',
			}),
			MuiFab: {
				size: 'small',
			},
			MuiFormControl: {
				margin: 'dense',
				size: 'small',
			},
			MuiFormHelperText: {
				margin: 'dense',
			},
			MuiGrid2: {
				defaultProps: {
					disableequaloverflow: "true",
					spacing: 2,
				},
			},
			MuiIconButton: {
				size: 'small',
			},
			MuiInputBase: {
				margin: 'dense',
			},
			MuiInputLabel: {
				margin: 'dense',
				// styleOverrides: {
				// 	root: ({ theme }) => ({
				// 		'&.Mui-focused': {
				// 			color: theme.palette.text.primary,
				// 		},
				// 	}),
				// },
			},
			MuiLink: {
				defaultProps: {
					color: palette.text.link,
					underline: 'hover',
				}
			},
			MuiList: {
				dense: true,
			},
			MuiMenuItem: {
				dense: true,
			},
			MuiOutlinedInput: {
				size: 'small',
				// styleOverrides: {
				// 	root: ({ theme }) => ({
				// 		'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
				// 			borderColor: theme.palette.text.primary,
				// 		},
				// 	}),
				// },
			},
			MuiPaper: {
				defaultProps: {
					elevation: 2,
				}
			},
			MuiRadio: {
				size: 'small',
			},
			MuiStack: {
				defaultProps: {
					spacing: 2,
					direction: "row",
				}
			},
			MuiSwitch: {
				size: 'small',
			},
			MuiTable: {
				size: 'small',
			},
			MuiTextField: {
				margin: 'dense',
				size: 'small',
			},
			MuiToggleButton: {
				size: 'small',
			},
			MuiToggleButtonGroup: {
				size: 'small',
			},
			/*
						
						MuiBadge: style(({ theme }) => ({
							'.MuiBadge-anchorOriginTopRight': {
								height: 14,
								minWidth: 14,
								width: 14,
							},
						})),
						MuiChip: style({ height: '20px' }),
						MuiIconButton: style({ padding: 4 }),
						MuiFab: style({ height: 36, width: 36 }),
						MuiTableCell: style({
							padding: '0 4px',
							// '&.MuiTableCell-head:not(.MuiTableCell-paddingCheckbox)': { padding: 4 },
						}),
						*/
		}
	});

	theme = responsiveFontSizes(theme);

	return (
		<UserThemeContext.Provider value={{ mode }}>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				{children}
			</ThemeProvider>
		</UserThemeContext.Provider>
	);
}